/**
 * PATH: apps/qa/src/pages/record-detail/ProgressSummary.tsx
 * PURPOSE: Inspection progress bars (checklist completion, evidence, hold points, NCRs)
 */
import { useState } from 'react'

export function ProgressSummary({ inspection }: { inspection: Record<string, unknown> }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="bg-card rounded-xl shadow-sm border border-border p-6">
      <button
        type="button"
        className="flex items-center gap-1 text-lg font-semibold text-foreground"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span aria-hidden>{open ? 'v' : '>'}</span>
        Progress
      </button>
      {open ? (
      <div className="space-y-4 mt-4">
        <ProgressBar
          label="Checklist Completion"
          value={`${inspection?.checklist_completion_percent ?? 0}%`}
          detail={inspection?.checklist_total ? `(${inspection.checklist_done ?? 0}/${inspection.checklist_total})` : undefined}
          percent={inspection?.checklist_completion_percent as number ?? 0}
          barColor="bg-primary"
        />
        <ProgressBar
          label="Evidence Captured"
          value={`${inspection?.evidence_count ?? 0} item${(inspection?.evidence_count as number ?? 0) !== 1 ? 's' : ''}`}
          percent={Math.min(((inspection?.evidence_count as number) ?? 0) * 10, 100)}
          barColor="bg-emerald-500"
        />
        <ProgressBar
          label="Hold Points"
          value={`${inspection?.hold_points_pending ?? 0} pending`}
          detail={(inspection?.hold_points_total as number ?? 0) > 0 ? `/ ${inspection?.hold_points_total} total` : undefined}
          percent={
            (inspection?.hold_points_total as number ?? 0) > 0
              ? ((1 - (inspection?.hold_points_pending as number ?? 0) / (inspection.hold_points_total as number)) * 100)
              : 0
          }
          barColor="bg-amber-500"
        />
        {((inspection?.ncr_open_count as number) ?? 0) > 0 && (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">Open NCRs</span>
              <span className="text-destructive font-medium">{inspection?.ncr_open_count as number}</span>
            </div>
          </div>
        )}
      </div>
      ) : null}
    </div>
  )
}

function ProgressBar({ label, value, detail, percent, barColor }: {
  label: string; value: string; detail?: string; percent: number; barColor: string
}) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-foreground font-medium">
          {value}
          {detail && <span className="text-muted-foreground font-normal ml-1">{detail}</span>}
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}
